/**
 * <ReferralShare> — referral link + share actions (organism, L0.1).
 *
 * Thin composer over referral molecules. Effects (copy, share, retry) are injected. Pendant que
 * le lien arrive, la forme de ce qui vient : la ligne du lien, les canaux, le rappel du filleul.
 */
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { DelayedSkeleton } from "../../atoms/Skeleton"
import { Text } from "../../atoms/typography/Text"
import { ReferralBenefitBanner } from "../../molecules/ReferralBenefitBanner"
import { ReferralLinkRow } from "../../molecules/ReferralLinkRow"
import { ReferralLinkRowSkeleton } from "../../molecules/ReferralLinkRow/ReferralLinkRowSkeleton"
import { ReferralShareActions, type ReferralShareActionItem } from "../../molecules/ReferralShareActions"
import { ReferralShareActionsSkeleton } from "../../molecules/ReferralShareActions/ReferralShareActionsSkeleton"
import { ReferralShareHeader } from "../../molecules/ReferralShareHeader"

export type ReferralShareLink = {
    url: string
    /** e.g. commission amount in euros — shown in the header subtitle. */
    rewardLabel?: string
}

export type ReferralShareAction = ReferralShareActionItem

export type ReferralShareProps = {
    loading?: boolean
    error?: boolean
    /** Share-channel failure message (e.g. app not installed) — shown under the body. */
    errorMessage?: string | null
    link?: ReferralShareLink | null
    shareActions?: ReferralShareAction[]
    /**
     * Rappelle ce que gagne le filleul. Défaut `true` — rendu inchangé pour la modale. Le tableau
     * de parrainage le passe à `false` : sa section « Comment ça marche » le dit déjà en entier.
     */
    showBenefit?: boolean
    onCopyLink?: () => void
    onRetry?: () => void
    testID?: string
}

export function ReferralShare(props: ReferralShareProps) {
    const {
        loading = false,
        error = false,
        errorMessage,
        link,
        shareActions = [],
        showBenefit = true,
        onCopyLink,
        onRetry,
        testID,
    } = props

    return (
        <Stack gap="0" testID={testID}>
            <ReferralShareHeader rewardLabel={link?.rewardLabel} />

            <Stack gap="4" style={styles.body}>
                {loading && (
                    <DelayedSkeleton accessibilityLabel="Chargement de votre lien">
                        <Stack gap="4">
                            <ReferralLinkRowSkeleton />
                            <ReferralShareActionsSkeleton />
                            {showBenefit && <ReferralBenefitBanner />}
                        </Stack>
                    </DelayedSkeleton>
                )}

                {!loading && error && (
                    <Stack gap="3" align="center" style={styles.centerBlock}>
                        <Text color="danger" center>
                            Impossible de charger votre lien de parrainage.
                        </Text>
                        {onRetry != null && <Button label="Réessayer" onPress={onRetry} />}
                    </Stack>
                )}

                {!loading && !error && link != null && (
                    <>
                        <ReferralLinkRow url={link.url} onCopyLink={onCopyLink} />
                        <ReferralShareActions actions={shareActions} />
                        {showBenefit && <ReferralBenefitBanner />}
                    </>
                )}

                {errorMessage != null && errorMessage.length > 0 && (
                    <Text size="text-sm" color="danger" center>
                        {errorMessage}
                    </Text>
                )}
            </Stack>
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    body: {
        padding: theme.spacing["4"],
    },
    centerBlock: {
        paddingVertical: theme.spacing["6"],
    },
}))
